/**
 * Consolidated formatting utilities for dates, durations, and activity metrics.
 * Replaces date-fns dependency with native Intl APIs.
 */

import { ActivityType } from '../types';

// Create a single RTF instance for Vietnamese locale
const rtfVi = new Intl.RelativeTimeFormat('vi', { numeric: 'auto' });

/**
 * Format a date as a relative time string in Vietnamese (e.g., "5 phút trước")
 * Native replacement for date-fns formatDistanceToNow with Vietnamese locale.
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = Date.now();
  const diffMs = d.getTime() - now;
  const diffSec = Math.round(diffMs / 1000);
  const absSec = Math.abs(diffSec);

  // Determine the best unit to use
  if (absSec < 60) {
    return rtfVi.format(diffSec, 'second');
  }
  if (absSec < 3600) {
    return rtfVi.format(Math.round(diffSec / 60), 'minute');
  }
  if (absSec < 86400) {
    return rtfVi.format(Math.round(diffSec / 3600), 'hour');
  }
  if (absSec < 2592000) { // ~30 days
    return rtfVi.format(Math.round(diffSec / 86400), 'day');
  }
  if (absSec < 31536000) { // ~365 days
    return rtfVi.format(Math.round(diffSec / 2592000), 'month');
  }
  return rtfVi.format(Math.round(diffSec / 31536000), 'year');
}

/**
 * Format a date as a short relative time string (e.g., "5p", "2h", "3d")
 * Compact format for space-constrained UI like comments.
 */
export function formatShortRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return 'vừa xong';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}p`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}d`;

  // Fall back to short date format
  return new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit' }).format(d);
}

/**
 * Format duration from seconds to human-readable string (e.g., "1h 30m", "45m 12s")
 */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);

  if (h > 0) return `${h}h ${m}m`;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/**
 * Get duration in seconds from an Activity, preferring precise durationSeconds field.
 */
export function getDurationSeconds(activity: { duration?: number; durationSeconds?: number }): number {
  return activity.durationSeconds ?? ((activity.duration ?? 0) * 60);
}

/**
 * Format pace or speed based on activity type.
 * - Run: pace in min/km (e.g., "5:30 /km")
 * - Ride: speed in km/h (e.g., "25.5 km/h")
 * - Swim: pace in min/100m (e.g., "2:15 /100m")
 */
export function formatPaceOrSpeed(activity: { type: ActivityType | string; distance: number; duration?: number; durationSeconds?: number }): string | null {
  const { type, distance } = activity;
  const durationSec = getDurationSeconds(activity);

  if (!distance || !durationSec) return null;

  if (type === ActivityType.RIDE) {
    const speed = distance / (durationSec / 3600); // km/h
    return `${speed.toFixed(1)} km/h`;
  }

  if (type === ActivityType.RUN) {
    const paceMin = (durationSec / 60) / distance; // min/km
    const mm = Math.floor(paceMin);
    const ss = Math.round((paceMin - mm) * 60);
    return `${mm}:${ss.toString().padStart(2, '0')} /km`;
  }

  if (type === ActivityType.SWIM) {
    const paceMin = (durationSec / 60) / (distance * 10); // min/100m
    const mm = Math.floor(paceMin);
    const ss = Math.round((paceMin - mm) * 60);
    return `${mm}:${ss.toString().padStart(2, '0')} /100m`;
  }

  return null;
}

/**
 * Format a date string to Vietnamese locale format with specific VN timezone.
 * @param format 'short' for DD/MM/YYYY, 'long' for full datetime
 */
export function formatDate(date: Date | string, format: 'short' | 'long' = 'short'): string {
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (format === 'long') return formatDateTimeVN(d);
    return formatDateVN(d);
  } catch {
    return typeof date === 'string' ? date : '';
  }
}

/**
 * Format date to "DD/MM/YYYY HH:mm" in Vietnam Time
 */
export function formatDateTimeVN(date: Date | string): string {
  return new Intl.DateTimeFormat('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(typeof date === 'string' ? new Date(date) : date);
}

/**
 * Format date to "DD/MM/YYYY" in Vietnam Time
 */
export function formatDateVN(date: Date | string): string {
  return new Intl.DateTimeFormat('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(typeof date === 'string' ? new Date(date) : date);
}

/**
 * Format time to "HH:mm" in Vietnam Time
 */
export function formatTimeVN(date: Date | string): string {
  return new Intl.DateTimeFormat('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(typeof date === 'string' ? new Date(date) : date);
}

/**
 * Format the time offset between two dates using adaptive units (minutes, hours, or days).
 * Returns a signed string like "+30m", "+2.5h", "-1d".
 */
export function formatHoursOffset(date1: Date, date2: Date): string {
  const diffMs = date1.getTime() - date2.getTime();
  const diffSec = Math.abs(diffMs / 1000);
  const sign = diffMs >= 0 ? '+' : '-';

  // Less than 1 hour: show minutes
  if (diffSec < 3600) {
    const minutes = Math.round(diffSec / 60);
    return `${sign}${minutes}m`;
  }

  // Less than 24 hours: show hours with 1 decimal
  if (diffSec < 86400) {
    const hours = diffSec / 3600;
    return `${sign}${hours.toFixed(1)}h`;
  }

  // 24+ hours: show days with 1 decimal
  const days = diffSec / 86400;
  return `${sign}${days.toFixed(1)}d`;
}

/**
 * Get date string (YYYY-MM-DD) in Vietnam timezone (Asia/Ho_Chi_Minh).
 * Ensures consistency with backend daily aggregation logic.
 */
export function getVietnamDateString(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-CA', { // en-CA gives YYYY-MM-DD
    timeZone: 'Asia/Ho_Chi_Minh',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);
}

/**
 * Get the hour (0-23) in Vietnam Time
 */
export function getVietnamHour(date: Date | string): number {
  const d = typeof date === 'string' ? new Date(date) : date;
  const hourStr = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Ho_Chi_Minh',
    hour: 'numeric',
    hour12: false
  }).format(d);
  return parseInt(hourStr, 10);
}

/**
 * Format date to "DD/MM" in Vietnam Time
 */
export function formatShortDateVN(date: Date | string): string {
  return new Intl.DateTimeFormat('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    day: '2-digit',
    month: '2-digit'
  }).format(typeof date === 'string' ? new Date(date) : date);
}
